/* eslint-disable react/jsx-pascal-case */
import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";

import { Translation as T } from "@awe-web/shared/lib";
import { B2C_SignUpGuest, B2C_STR_SelectRentObject } from "../../../routing/routes.js";
import { ScreenLoading, ScreenSection } from "../../../components/screen.js";
import { useInitialize } from "../../../util/react.js";
import { Button } from "../../../components/form.js";
import { Col, Row } from "../../../components/grid.js";
import { TypeArea } from "../../../components/layout.js";

import SelectedRentObject from "../../../rent_objects/components/selected_rent_object";
import SelectedStationAndTime from "../../../stations/components/selected_station_and_time.jsx";
import STR_SelectExtrasForm from "../../../rent_contracts/components/str_select_extras_form.jsx";

import {
  getStrDeductibleOptions,
  getStrSelectedEquipmentOptions,
  getStrSelectedMileageOptions
} from "../../../rent_contracts/store.js";
import { initialize, submit } from "./actions.js";

import "./select_extras.scss";

const BACK = "ALG0000030";
const INCLUDING_VAT = "FSD0000263";
const PRICES_NOTICE = "ALG0000244";

function InternalSelectExtrasForm({ onSubmit, onGuestCheck }) {
  const deductibleOptions = useSelector((state) => getStrDeductibleOptions(state));
  const selectedMileageOptions = useSelector((state) => getStrSelectedMileageOptions(state));
  const selectedEquipmentOptions = useSelector((state) => getStrSelectedEquipmentOptions(state));

  const enabledDeductible = useMemo(() => {
    return deductibleOptions?.toJS().find(({ enabled }) => enabled);
  }, [deductibleOptions]);

  const initialValues = {
    mileage: selectedMileageOptions?.toJS(),
    deductible: enabledDeductible && enabledDeductible.id,
    equipment: selectedEquipmentOptions?.toJS()
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} >
      {(formikProps) => (
        <STR_SelectExtrasForm
          onGuestCheck={onGuestCheck}
          {...formikProps}
          reservationPath="B2C"
          basePriceHintCode={INCLUDING_VAT}
          deductibleOptions={deductibleOptions?.toJS()}
        />
      )}
    </Formik>
  );
}

export default function B2C_STR_SelectExtras() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isGuestCheckout, setIsGuestCheckout] = useState(false);

  const handleCheckboxChange = useCallback((event) => {
    setIsGuestCheckout((value) => {
      if (event.target?.checked === undefined) {
        return value;
      }
      else {
        return event.target?.checked;
      }

    });
  }, []);

  const onSubmit = useCallback(async (values) => {
    const nextPath = await dispatch(submit(values));
    if (isGuestCheckout) {
      navigate(B2C_SignUpGuest.path(), { state: { from: location } });
    }
    else if (nextPath && nextPath.length > 0) {
      navigate(nextPath);
    }
  }, [dispatch, isGuestCheckout, location, navigate]);

  const init = useCallback(async () => {
    const redirectPath = await dispatch(initialize());

    if (redirectPath && redirectPath.length > 0) {
      navigate(redirectPath);
    }
  }, [dispatch, navigate]);

  const isReady = useInitialize(init);
  if (!isReady) {
    return <ScreenLoading />;
  }

  return (
    <div className="b2c-str-select-extras">
      <ScreenSection dark>
        <TypeArea>
          <SelectedStationAndTime reservationPath="B2C" />
        </TypeArea>
      </ScreenSection>

      <ScreenSection light>
        <TypeArea>
          <SelectedRentObject
            className="b2c-str-select-extras__selected-rent-object"
            selectedBasicProperties="B2C"
            action={(
              <Button secondary back to={B2C_STR_SelectRentObject.path()}>
                <T code={BACK} />
              </Button>
            )}
          />
        </TypeArea>
      </ScreenSection>

      <InternalSelectExtrasForm onSubmit={onSubmit} onGuestCheck={handleCheckboxChange} />

      <TypeArea>
        <ScreenSection className="b2c-str-select-extras__prices-notice">
          <Row>
            <Col desktop={{ offset: 1 }}>
              <T code={PRICES_NOTICE} />
            </Col>
          </Row>
        </ScreenSection>
      </TypeArea>
    </div>
  );
}
